import firebase from "firebase";
import { Lecture } from "../../types/lecture";
import { LectureServiceInterface } from "./LectureServiceInterface";
export class LectureServiceFirestore extends LectureServiceInterface {
    static LECTURES_COLLECTION = "lectures";
    firestore;
    constructor(firestore) {
        super();
        this.firestore = firestore;
    }
    toLecture(doc) {
        const data = doc.data();
        // Handle potential undefined date fields
        if (!data.lectureDate || !data.lectureEndDate) {
            throw new Error(`Lecture document ${doc.id} is missing required date fields`);
        }
        const lectureData = {
            title: data.title,
            teacherId: data.teacherId,
            firstName: data.firstName,
            lastName: data.lastName,
            classroomId: data.classroomId,
            topicIds: data.topicIds ?? [],
            weekNumber: data.weekNumber,
            lectureDate: data.lectureDate.toDate(),
            lectureEndDate: data.lectureEndDate.toDate(),
            status: data.status,
            tournamentId: data.tournamentId,
        };
        const lecture = new Lecture(lectureData);
        lecture.id = doc.id;
        if (data.attendanceRecords) {
            lecture.attendanceRecords = data.attendanceRecords.map((record) => ({
                ...record,
                timestamp: record.timestamp?.toDate() || new Date(),
            }));
        }
        return lecture;
    }
    async getAll() {
        const snapshot = await this.firestore
            .collection(LectureServiceFirestore.LECTURES_COLLECTION)
            .get();
        return snapshot.docs
            .map((doc) => this.toLecture(doc))
            .sort((a, b) => a.lectureDate.getTime() - b.lectureDate.getTime());
    }
    async getByClassId(classroomId) {
        const snapshot = await this.firestore
            .collection(LectureServiceFirestore.LECTURES_COLLECTION)
            .where("classroomId", "==", classroomId)
            .get();
        return snapshot.docs
            .map((doc) => this.toLecture(doc))
            .sort((a, b) => a.lectureDate.getTime() - b.lectureDate.getTime());
    }
    async getLecture(lectureId) {
        const doc = await this.firestore
            .collection(LectureServiceFirestore.LECTURES_COLLECTION)
            .doc(lectureId)
            .get();
        if (!doc.exists) {
            throw new Error(`Lecture with id ${lectureId} not found`);
        }
        return this.toLecture(doc);
    }
    async createLecture(lectureData) {
        const lecture = new Lecture(lectureData);
        const lectureForFirestore = {
            classroomId: lecture.classroomId,
            topicIds: lecture.topicIds,
            weekNumber: lecture.weekNumber,
            lectureDate: firebase.firestore.Timestamp.fromDate(lecture.lectureDate),
            lectureEndDate: firebase.firestore.Timestamp.fromDate(lecture.lectureEndDate), // TODO: recheck this when create lecture
            status: lecture.status,
            teacherId: lecture.teacherId,
            firstName: lecture.firstName,
            lastName: lecture.lastName,
            attendanceRecords: lecture.attendanceRecords?.map((record) => ({
                ...record,
                timestamp: firebase.firestore.Timestamp.fromDate(record.timestamp),
            })) ?? [],
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        };
        await this.firestore
            .collection(LectureServiceFirestore.LECTURES_COLLECTION)
            .doc(lecture.id)
            .set(lectureForFirestore);
        return lecture;
    }
    async startLecture(lectureId, teacherId) {
        if (!teacherId) {
            throw new Error("teacherId is required to start a lecture");
        }
        const lecture = await this.getLecture(lectureId);
        lecture.start(teacherId);
        await this.firestore
            .collection(LectureServiceFirestore.LECTURES_COLLECTION)
            .doc(lectureId)
            .update({
            status: lecture.status,
            teacherId: lecture.teacherId,
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
        return lecture;
    }
    async completeLecture(lectureId) {
        const lecture = await this.getLecture(lectureId);
        lecture.complete();
        await this.firestore
            .collection(LectureServiceFirestore.LECTURES_COLLECTION)
            .doc(lectureId)
            .update({
            status: lecture.status,
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
        return lecture;
    }
    async setAttendanceRecords(lectureId, records) {
        const lecture = await this.getLecture(lectureId);
        lecture.setAttendanceRecords(records);
        await this.firestore
            .collection(LectureServiceFirestore.LECTURES_COLLECTION)
            .doc(lectureId)
            .update({
            attendanceRecords: records.map((record) => ({
                ...record,
                timestamp: firebase.firestore.Timestamp.fromDate(record.timestamp),
            })),
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
        return lecture;
    }
    async updateLecture(lectureId, lectureData) {
        const lecture = await this.getLecture(lectureId);
        lecture.update(lectureData);
        const updateData = {
            topicIds: lecture.topicIds,
            weekNumber: lecture.weekNumber,
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        };
        if (lectureData.lectureDate) {
            updateData.lectureDate = firebase.firestore.Timestamp.fromDate(lecture.lectureDate);
        }
        await this.firestore
            .collection(LectureServiceFirestore.LECTURES_COLLECTION)
            .doc(lectureId)
            .update(updateData);
        return lecture;
    }
    async setTournamentId(lectureId, tournamentId) {
        await this.firestore
            .collection(LectureServiceFirestore.LECTURES_COLLECTION)
            .doc(lectureId)
            .update({
            tournamentId,
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
    }
}
